import { useState } from "react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, addDays,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, ChevronRight } from "lucide-react";

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CalendarEventItem {
  id: number;
  date: string;
  title: string;
  color: string;
}

interface SlotShape {
  id: number;
  day: string;
  label: string;
  slotType: string;
  courseColor?: string | null;
  courseName?: string | null;
}

interface TimetableDetail {
  weeks?: Array<{ weekLabel: string; slots: SlotShape[] }>;
}

interface Props {
  month: Date;
  timetable?: { id: number; name: string; startDate: string } | null;
  timetableDetail?: TimetableDetail | null;
  events: CalendarEventItem[];
  onCreateEvent: (date: string, title: string, color: string) => Promise<void>;
  onDeleteEvent: (id: number) => Promise<void>;
  onUpdateEvent: (id: number, title: string, color: string) => Promise<void>;
  onNavigateToDay: (date: Date) => void;
}

// ─── Event colour palette ──────────────────────────────────────────────────────

const EVENT_COLORS = [
  { label: "Blue",   value: "#3b82f6" },
  { label: "Indigo", value: "#6366f1" },
  { label: "Purple", value: "#a855f7" },
  { label: "Pink",   value: "#ec4899" },
  { label: "Red",    value: "#ef4444" },
  { label: "Orange", value: "#f97316" },
  { label: "Amber",  value: "#f59e0b" },
  { label: "Green",  value: "#22c55e" },
  { label: "Teal",   value: "#14b8a6" },
  { label: "Gray",   value: "#6b7280" },
];

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getWeekIndex(startDate: string, date: Date): number {
  const start = new Date(startDate + "T00:00:00");
  const startMonday = addDays(start, -((start.getDay() + 6) % 7));
  const targetMonday = addDays(date, -((date.getDay() + 6) % 7));
  const diff = Math.round((targetMonday.getTime() - startMonday.getTime()) / (7 * 86400000));
  return Math.max(0, diff);
}

const DAY_NAME_MAP: Record<number, string> = {
  1: "monday", 2: "tuesday", 3: "wednesday", 4: "thursday", 5: "friday", 6: "saturday", 0: "sunday",
};

function getSlotsForDate(timetable: Props["timetable"], detail: TimetableDetail | null | undefined, date: Date): SlotShape[] {
  if (!timetable || !detail?.weeks?.length) return [];
  const weekCount = detail.weeks.length;
  const weekIndex = getWeekIndex(timetable.startDate, date) % weekCount;
  const week = detail.weeks[weekIndex];
  if (!week) return [];
  const dayName = DAY_NAME_MAP[date.getDay()];
  return week.slots.filter((s) => s.day === dayName && s.slotType === "period");
}

// ─── Day cell detail popover ──────────────────────────────────────────────────

interface DayPopoverProps {
  date: Date;
  events: CalendarEventItem[];
  onAdd: (title: string, color: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onEdit: (id: number, title: string, color: string) => Promise<void>;
  onNavigate: () => void;
}

function DayPopover({ date, events, onAdd, onDelete, onEdit, onNavigate }: DayPopoverProps) {
  const [newTitle, setNewTitle] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editColor, setEditColor] = useState("#3b82f6");

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    setSaving(true);
    await onAdd(newTitle.trim(), newColor);
    setNewTitle("");
    setNewColor("#3b82f6");
    setSaving(false);
  };

  const startEdit = (ev: CalendarEventItem) => {
    setEditingId(ev.id);
    setEditTitle(ev.title);
    setEditColor(ev.color);
  };

  const handleSaveEdit = async () => {
    if (editingId == null || !editTitle.trim()) return;
    await onEdit(editingId, editTitle.trim(), editColor);
    setEditingId(null);
  };

  return (
    <div className="w-72 p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm text-gray-900">{format(date, "EEEE d MMMM")}</span>
        <button
          onClick={onNavigate}
          className="text-xs text-blue-600 flex items-center gap-0.5 hover:underline"
        >
          View day <ChevronRight className="h-3 w-3" />
        </button>
      </div>

      {/* Existing events */}
      {events.length > 0 && (
        <div className="space-y-1">
          {events.map((ev) => (
            <div key={ev.id} className="group">
              {editingId === ev.id ? (
                <div className="space-y-1.5 p-2 bg-gray-50 rounded-lg">
                  <input
                    className="w-full text-sm border border-gray-200 rounded px-2 py-1 outline-none focus:border-blue-400"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
                    autoFocus
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                    {EVENT_COLORS.map((c) => (
                      <button
                        key={c.value}
                        title={c.label}
                        onClick={() => setEditColor(c.value)}
                        className="w-4 h-4 rounded-full border-2 transition-transform hover:scale-110"
                        style={{
                          backgroundColor: c.value,
                          borderColor: editColor === c.value ? "#1e293b" : "transparent",
                        }}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" className="h-6 text-xs flex-1" onClick={handleSaveEdit}>Save</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => startEdit(ev)}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color }} />
                  <span className="text-sm text-gray-800 flex-1 truncate">{ev.title}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(ev.id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add new event */}
      <div className="pt-1 border-t border-gray-100 space-y-2">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Add event</p>
        <input
          className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-blue-400"
          placeholder="Event title…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <div className="flex items-center gap-1 flex-wrap">
          {EVENT_COLORS.map((c) => (
            <button
              key={c.value}
              title={c.label}
              onClick={() => setNewColor(c.value)}
              className="w-4 h-4 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                backgroundColor: c.value,
                borderColor: newColor === c.value ? "#1e293b" : "transparent",
              }}
            />
          ))}
        </div>
        <Button
          size="sm"
          className="w-full h-7 text-xs gap-1"
          onClick={handleAdd}
          disabled={!newTitle.trim() || saving}
        >
          <Plus className="h-3.5 w-3.5" />
          Add event
        </Button>
      </div>
    </div>
  );
}

// ─── Main MonthView component ─────────────────────────────────────────────────

export function MonthView({ month, timetable, timetableDetail, events, onCreateEvent, onDeleteEvent, onUpdateEvent, onNavigateToDay }: Props) {
  const [openDate, setOpenDate] = useState<string | null>(null);

  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const allDays = eachDayOfInterval({ start: calStart, end: calEnd });

  // Group events by date string
  const eventsByDate = new Map<string, CalendarEventItem[]>();
  for (const ev of events) {
    const key = ev.date;
    if (!eventsByDate.has(key)) eventsByDate.set(key, []);
    eventsByDate.get(key)!.push(ev);
  }

  const today = new Date();

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-200">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 divide-x divide-gray-100">
        {allDays.map((day, i) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const inMonth = isSameMonth(day, month);
          const isCurrentDay = isSameDay(day, today);
          const dayEvents = eventsByDate.get(dateStr) ?? [];
          const periods = getSlotsForDate(timetable, timetableDetail, day);
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          const isOpen = openDate === dateStr;

          return (
            <div
              key={dateStr}
              className={[
                "min-h-[100px] p-1.5 border-b border-gray-100 relative",
                !inMonth ? "bg-gray-50/50" : isWeekend ? "bg-slate-50/40" : "",
                i % 7 === 6 ? "border-r-0" : "",
              ].filter(Boolean).join(" ")}
            >
              {/* Date number */}
              <Popover open={isOpen} onOpenChange={(open) => setOpenDate(open ? dateStr : null)}>
                <PopoverTrigger asChild>
                  <button
                    className={[
                      "w-6 h-6 rounded-full text-xs font-medium flex items-center justify-center transition-colors",
                      isCurrentDay
                        ? "bg-blue-600 text-white font-bold"
                        : inMonth
                          ? "text-gray-800 hover:bg-blue-100 hover:text-blue-700"
                          : "text-gray-300 hover:bg-gray-100",
                    ].join(" ")}
                  >
                    {format(day, "d")}
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" align="start" className="p-0 w-auto" sideOffset={4}>
                  <DayPopover
                    date={day}
                    events={dayEvents}
                    onAdd={async (title, color) => {
                      await onCreateEvent(dateStr, title, color);
                    }}
                    onDelete={async (id) => { await onDeleteEvent(id); }}
                    onEdit={async (id, title, color) => { await onUpdateEvent(id, title, color); }}
                    onNavigate={() => {
                      setOpenDate(null);
                      onNavigateToDay(day);
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Period dots */}
              {periods.length > 0 && inMonth && (
                <div className="flex flex-wrap gap-0.5 mt-0.5 mb-1">
                  {periods.slice(0, 5).map((s) => (
                    <span
                      key={s.id}
                      title={s.courseName ?? s.label}
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: s.courseColor ?? "#94a3b8" }}
                    />
                  ))}
                </div>
              )}

              {/* Events */}
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map((ev) => (
                  <button
                    key={ev.id}
                    onClick={() => setOpenDate(dateStr)}
                    className="w-full text-left text-[11px] leading-tight px-1.5 py-0.5 rounded truncate font-medium"
                    style={{ backgroundColor: ev.color + "22", color: ev.color }}
                  >
                    {ev.title}
                  </button>
                ))}
                {dayEvents.length > 3 && (
                  <button
                    onClick={() => setOpenDate(dateStr)}
                    className="text-[10px] text-gray-400 px-1 hover:text-gray-600"
                  >
                    +{dayEvents.length - 3} more
                  </button>
                )}
              </div>

              {/* Quick add button on hover */}
              {inMonth && dayEvents.length === 0 && (
                <button
                  onClick={() => setOpenDate(dateStr)}
                  className="absolute bottom-1 right-1 opacity-0 hover:opacity-100 group-hover:opacity-100 text-gray-300 hover:text-blue-500 transition-opacity"
                  style={{ opacity: isOpen ? 1 : undefined }}
                >
                  <Plus className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
